// Sentry frontend — Round C 2026-04-28.
//
// Activación: setear `REACT_APP_SENTRY_DSN` en el build del frontend
// (CRA inyecta `REACT_APP_*` en el bundle al `npm run build`). Sin DSN,
// `initSentry()` es no-op — útil para dev local y CI.
//
// Provider: `@sentry/react` v9. Captura:
//   - Excepciones de React (vía ErrorBoundary)
//   - Promesas no atrapadas
//   - Errores de fetch (con browserTracing integrations)
//   - Performance (sample 10% de pageloads)

import * as Sentry from "@sentry/react";
import { getDisplayMode } from "../utils/displayMode";
import type { LoginFailure } from "../utils/loginErrors";

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development";
// Releases: si Railway inyecta el commit SHA, lo usamos. Sino, "dev".
const RELEASE = process.env.REACT_APP_SENTRY_RELEASE || "dev";

let initialized = false;

/**
 * Inicializa Sentry. Llamar UNA vez al boot del frontend, antes del
 * `createRoot().render()`. Idempotente.
 */
export function initSentry(): void {
  if (initialized) return;
  if (!DSN) {
    // No log a consola — el dashboard del browser ya está saturado en dev.
    return;
  }

  try {
    Sentry.init({
      dsn: DSN,
      environment: ENV,
      release: RELEASE,
      // Browser performance tracing: 10% de pageloads. Sin esto, solo
      // capturamos errores (suficiente para empezar pero perdemos
      // contexto sobre lentitud de páginas).
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1),
      // PII: el frontend tiene access al user via AuthContext. NO
      // queremos enviar datos del usuario a Sentry sin pensarlo. Dejamos
      // sendDefaultPii=false; si después decidimos enviar el userId
      // para correlacionar errores con usuarios concretos, lo hacemos
      // explícito vía Sentry.setUser({ id }) en AuthContext.
      sendDefaultPii: false,
      // Filtrar: errores de extensiones de browser, requests cancelados.
      ignoreErrors: [
        // ResizeObserver loop limit exceeded — falso positivo común.
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // Network errors (los manejamos vía toast, no son bugs).
        "NetworkError",
        "Failed to fetch",
        // Browser extensions
        "TopLevelHandler",
      ],
    });
    initialized = true;
    // Etiqueta global (round 2026-10-28): «¿esto pasa solo en la app instalada?» se responde
    // filtrando por display-mode=standalone|browser en cualquier evento, no solo en el login.
    Sentry.setTag("display-mode", getDisplayMode());
  } catch (err) {
    // No tirar — un error de Sentry no debe tumbar la app.
    // eslint-disable-next-line no-console
    console.warn("[sentry] init falló:", err);
  }
}

/**
 * Wrapper alrededor de `Sentry.captureException` con guard de DSN.
 * Útil cuando un componente cacha un error y quiere reportarlo
 * manualmente sin romper la UI.
 */
export function captureError(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
      Sentry.captureException(err);
    });
  } else {
    Sentry.captureException(err);
  }
}

/**
 * Reporta un fallo de inicio de sesión a Sentry (round 2026-10-28), con la causa ya
 * clasificada y la etiqueta display-mode. Sirve para responder «¿por qué la app instalada
 * no me deja entrar?» sin tener el celular en la mano.
 *
 * Privacidad: NO se envía el correo, la contraseña ni el token de Turnstile — solo la
 * categoría, el estado HTTP, el código del backend y datos del entorno (origen de la página,
 * host de la API, modo de visualización, conectividad).
 *
 * Nivel: credenciales incorrectas es «info» (lo normal de un tipeo errado); caídas del
 * servidor, «error»; el resto «warning».
 */
export function reportarFalloLogin(
  fallo: Pick<LoginFailure, "kind" | "status" | "code" | "retryAfterSeconds">,
  contexto: { apiBase?: string } = {},
): void {
  if (!initialized) return;
  const modo = getDisplayMode();
  const nivel = fallo.kind === "credenciales" ? "info" : fallo.kind === "servidor" ? "error" : "warning";
  let apiHost: string | undefined;
  try {
    apiHost = contexto.apiBase ? new URL(contexto.apiBase, window.location.origin).host : undefined;
  } catch {
    apiHost = undefined;
  }
  Sentry.withScope((scope) => {
    scope.setLevel(nivel);
    scope.setTag("display-mode", modo);
    scope.setTag("login-fallo", fallo.kind);
    if (fallo.status !== undefined) scope.setTag("http-status", String(fallo.status));
    if (fallo.code) scope.setTag("login-code", fallo.code);
    scope.setFingerprint(["login-fallo", fallo.kind, modo]);
    scope.setContext("login", {
      kind: fallo.kind,
      status: fallo.status ?? null,
      code: fallo.code ?? null,
      retryAfterSeconds: fallo.retryAfterSeconds ?? null,
      displayMode: modo,
      pageOrigin: typeof window !== "undefined" ? window.location.origin : null,
      apiHost: apiHost ?? null,
      online: typeof navigator !== "undefined" ? navigator.onLine : null,
    });
    Sentry.captureMessage(`Fallo de inicio de sesión: ${fallo.kind}`);
  });
}

/**
 * Identifica al usuario logueado para correlacionar errores con
 * usuarios reales en el dashboard. Llamar desde AuthContext después
 * de un login exitoso. `null` para limpiar (logout).
 */
export function setSentryUser(user: { id: number; email?: string; rol?: string } | null): void {
  if (!initialized) return;
  if (user) {
    // Solo id + rol. Email queda fuera por privacy — el id es
    // suficiente para correlacionar y no expone PII.
    Sentry.setUser({ id: String(user.id), segment: user.rol });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
